/**
 * Kiosk audio bleeps.
 *
 * Workers at the kiosk often look at the radio (not the screen) at the moment
 * of scan, so the only cue they reliably catch is sound (launch-plan I22).
 * Each tone is intentionally short and uses the Web Audio API directly —
 * no audio file dependency, no caching, no autoplay-permission dance beyond
 * the browser's per-tab unlock on first user gesture (which the kiosk
 * already has by the time a scan completes).
 *
 * Tones intentionally distinct:
 *   - success: a bright 880Hz double-pip ("ding-ding")
 *   - warning: a single mid 660Hz tone
 *   - error:   a low 220Hz buzz
 *
 * If the AudioContext can't be constructed (older browser, headless tests,
 * permission denied) the helper silently no-ops.
 */

type BleepKind = 'success' | 'warning' | 'error';

let sharedCtx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (sharedCtx) return sharedCtx;
  if (typeof window === 'undefined') return null;
  const Ctor: typeof AudioContext | undefined =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    sharedCtx = new Ctor();
  } catch {
    return null;
  }
  return sharedCtx;
}

interface ToneStep {
  freq: number;
  duration: number;
  delay: number;
}

const TONE_PROGRAMS: Record<BleepKind, ToneStep[]> = {
  success: [
    { freq: 880, duration: 0.09, delay: 0 },
    { freq: 1320, duration: 0.12, delay: 0.10 },
  ],
  warning: [{ freq: 660, duration: 0.22, delay: 0 }],
  error: [{ freq: 220, duration: 0.32, delay: 0 }],
};

function playStep(ctx: AudioContext, step: ToneStep, startAt: number) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.type = 'sine';
  osc.frequency.value = step.freq;

  // Linear attack-decay envelope so the pip doesn't click.
  const attack = 0.005;
  const release = Math.max(0.04, step.duration - attack);
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.linearRampToValueAtTime(0.18, startAt + attack);
  gain.gain.linearRampToValueAtTime(0.0001, startAt + attack + release);

  osc.connect(gain).connect(ctx.destination);
  osc.start(startAt);
  osc.stop(startAt + attack + release + 0.01);
}

export function playBleep(kind: BleepKind): void {
  const ctx = getCtx();
  if (!ctx) return;
  // Browsers suspend the context until the first user gesture; resume() is a
  // no-op if it's already running.
  if (ctx.state === 'suspended') {
    void ctx.resume().catch(() => undefined);
  }
  const program = TONE_PROGRAMS[kind];
  const now = ctx.currentTime;
  for (const step of program) {
    playStep(ctx, step, now + step.delay);
  }
}
